/* Hala Heritage — theme behaviour. No dependencies. */
(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents) {
    const format = window.theme.moneyFormat || '${{amount}}';
    const value = cents / 100;
    const withDelims = (n, decimals, thousands, decimal) => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelims(value, 0, '.', ',');
        default:
          return withDelims(value, 2, ',', '.');
      }
    });
  }

  function announce(message) {
    const region = $('#LiveRegion');
    if (region) region.textContent = message;
  }

  /* Scroll reveal --------------------------------------------------------- */

  // Content is visible by default. Motion is switched on only once this script is running,
  // and only for elements still below the fold, so nothing on screen ever waits for JavaScript.
  const motionEnabled = !reduceMotion && 'IntersectionObserver' in window;

  function initReveal(root = document) {
    const items = $$('[data-reveal]:not(.is-in)', root);
    if (!items.length) return;
    if (!motionEnabled) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const fold = window.innerHeight;
    const onScreen = items.filter((el) => el.getBoundingClientRect().top < fold);
    onScreen.forEach((el) => el.classList.add('is-in'));
    document.documentElement.classList.add('motion');
    const observer = new IntersectionObserver(
      (entries) => {
        // Items that arrive together (a row of cards) come in one after another.
        let order = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!entry.target.style.getPropertyValue('--i')) entry.target.style.setProperty('--i', Math.min(order++, 5));
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
  }

  /* Header ---------------------------------------------------------------- */

  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();

    // Desktop dropdowns: one open at a time, close on outside click or Escape.
    const drops = $$('[data-nav-drop]');
    drops.forEach((drop) => {
      drop.addEventListener('toggle', () => {
        if (drop.open) drops.forEach((other) => other !== drop && (other.open = false));
      });
    });
    document.addEventListener('click', (event) => {
      drops.forEach((drop) => {
        if (drop.open && !drop.contains(event.target)) drop.open = false;
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      drops.forEach((drop) => {
        if (!drop.open) return;
        drop.open = false;
        $('summary', drop).focus();
      });
    });
  }

  function initAnnouncements() {
    const bar = $('[data-announce]');
    if (!bar) return;
    const items = $$('.announce__item', bar);
    if (items.length < 2 || reduceMotion) return;
    let index = 0;
    setInterval(() => {
      if (document.hidden || bar.matches(':hover, :focus-within')) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, 4500);
  }

  /* Drawers --------------------------------------------------------------- */

  const drawers = {
    active: null,
    trigger: null,

    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer || this.active === drawer) return;
      if (this.active) this.close(false);
      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.hidden = false;
      // Force a frame so the opening transition runs from the hidden state.
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.classList.add('is-locked');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      const focusTarget = $('input[type="search"]', drawer) || $('[data-drawer-close]:not(.drawer__scrim)', drawer);
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
    },

    close(restoreFocus = true) {
      const drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      const trigger = this.trigger;
      this.active = null;
      this.trigger = null;
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 300);
      if (trigger && trigger.setAttribute) {
        if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus && trigger.focus) trigger.focus({ preventScroll: true });
      }
    },

    trapFocus(event) {
      const drawer = this.active;
      if (!drawer || event.key !== 'Tab') return;
      const focusable = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, summary', drawer).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  };

  function initDrawers() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        const id = opener.dataset.drawerOpen;
        // On the cart page the cart icon is a plain link.
        if (id === 'CartDrawer' && window.theme.template === 'cart') return;
        event.preventDefault();
        drawers.open(id, opener);
        return;
      }
      if (event.target.closest('[data-drawer-close]')) drawers.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') drawers.close();
      drawers.trapFocus(event);
    });
  }

  /* Cart ------------------------------------------------------------------ */

  const cart = {
    sectionIds() {
      return $$('[data-cart-section]').map((el) => el.dataset.sectionId);
    },

    render(sections) {
      if (!sections) return;
      let count = null;
      $$('[data-cart-section]').forEach((target) => {
        const html = sections[target.dataset.sectionId];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-cart-section]', doc);
        if (!fresh) return;
        target.innerHTML = fresh.innerHTML;
        target.dataset.itemCount = fresh.dataset.itemCount;
        count = Number(fresh.dataset.itemCount);
      });
      if (count !== null) this.setCount(count);
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
        el.classList.remove('is-bumped');
        // Restart the bump animation.
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      return data;
    },

    async add(items) {
      const data = await this.request(window.theme.routes.cartAdd, {
        items,
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      });
      this.render(data.sections);
      announce('Added to cart');
      if (window.theme.template !== 'cart') drawers.open('CartDrawer');
      return data;
    },

    async change(line, quantity) {
      const containers = $$('[data-cart-section]');
      containers.forEach((el) => el.classList.add('is-updating'));
      try {
        const data = await this.request(window.theme.routes.cartChange, {
          line,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        });
        this.render(data.sections);
        announce('Cart updated');
      } catch (error) {
        announce(error.message);
      } finally {
        containers.forEach((el) => el.classList.remove('is-updating'));
      }
    },
  };

  function initCart() {
    // Product forms
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      // Let express checkout buttons and other submitters behave natively.
      if (event.submitter && event.submitter.name !== 'add' && !event.submitter.hasAttribute('data-sticky-button')) return;
      event.preventDefault();

      const buttons = $$('[data-add-button], [data-sticky-button]');
      const errorEl = $('[data-form-error]', form);
      const formData = new FormData(form);
      const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      if (errorEl) errorEl.hidden = true;
      buttons.forEach((button) => button.classList.add('is-loading'));
      try {
        await cart.add([item]);
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } finally {
        buttons.forEach((button) => button.classList.remove('is-loading'));
      }
    });

    document.addEventListener('click', async (event) => {
      // Quick add from cards and add-on lists
      const quick = event.target.closest('[data-quick-add]');
      if (quick) {
        event.preventDefault();
        quick.classList.add('is-loading');
        try {
          await cart.add([{ id: Number(quick.dataset.quickAdd), quantity: 1 }]);
          quick.classList.add('is-added');
          setTimeout(() => quick.classList.remove('is-added'), 1600);
        } catch (error) {
          announce(error.message);
          window.location.href = window.theme.routes.cart;
        } finally {
          quick.classList.remove('is-loading');
        }
        return;
      }

      // Line quantity and remove
      const lineButton = event.target.closest('[data-cart-section] [data-line]');
      if (lineButton) {
        event.preventDefault();
        cart.change(Number(lineButton.dataset.line), Math.max(0, Number(lineButton.dataset.qty)));
        return;
      }

      // Quantity stepper on the product form
      const step = event.target.closest('[data-qty-step]');
      if (step) {
        const input = $('.qty__input', step.parentElement);
        input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* Product page ---------------------------------------------------------- */

  function initProduct() {
    const root = $('[data-product]');
    if (!root) return;
    const variants = JSON.parse($('[data-variants]', root).textContent);
    const picker = $('[data-variant-picker]', root);
    const idInput = $('[data-variant-id]', root);
    const priceEl = $('[data-price]', root);
    const track = $('[data-gallery-track]', root);
    const thumbs = $$('[data-gallery-thumb]', root);
    const currentEl = $('[data-gallery-current]', root);
    const sticky = $('[data-sticky-atc]');
    const addButton = $('[data-add-button]', root);

    // Gallery
    const slides = track ? $$('.gallery__slide', track) : [];
    const goTo = (index) => {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    if (slides.length > 1 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = slides.indexOf(entry.target);
            if (currentEl) currentEl.textContent = index + 1;
            thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb))));

    // Variants
    const selectedOptions = () => {
      const values = [];
      $$('input[type="radio"]:checked', picker).forEach((input) => {
        values[Number(input.dataset.optionIndex)] = input.value;
      });
      return values;
    };

    const updateAvailability = (selected) => {
      $$('input[type="radio"]', picker).forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const candidate = selected.slice();
        candidate[index] = input.value;
        const match = variants.find((v) => v.options.every((value, i) => value === candidate[i]));
        input.closest('label').classList.toggle('is-unavailable', !match || !match.available);
      });
    };

    const setVariant = (variant) => {
      const labels = $$('[data-add-label]');
      const stickyButton = $('[data-sticky-button]');
      if (!variant) {
        addButton.disabled = true;
        if (stickyButton) stickyButton.disabled = true;
        labels.forEach((el) => (el.textContent = 'Unavailable'));
        $('[data-add-price]', root).textContent = '';
        return;
      }
      idInput.value = variant.id;
      addButton.disabled = !variant.available;
      if (stickyButton) stickyButton.disabled = !variant.available;
      labels.forEach((el) => (el.textContent = variant.available ? 'Add to cart' : 'Sold out'));
      $('[data-add-price]', root).textContent = variant.available ? formatMoney(variant.price) : '';

      if (priceEl) {
        const onSale = variant.compare_at_price > variant.price;
        const saving = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
        priceEl.innerHTML =
          `<span class="price${onSale ? ' price--sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare"><span class="sr-only">Regular price</span>${formatMoney(variant.compare_at_price)}</s>` : '') +
          '</span>' +
          (onSale ? `<span class="badge badge--accent">Save ${saving}%</span>` : '');
      }
      const stickyPrice = $('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      if (variant.featured_media) {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.featured_media.id));
        if (index > -1) goTo(index);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    if (picker) {
      picker.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"]')) return;
        const label = $('[data-option-label]', event.target.closest('fieldset'));
        if (label) label.textContent = event.target.value;
        const selected = selectedOptions();
        const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));
        updateAvailability(selected);
        setVariant(variant);
      });
      updateAvailability(selectedOptions());
    }

    // Sticky add to cart: show once the main button has scrolled out of view.
    if (sticky && addButton && 'IntersectionObserver' in window) {
      const stickyButton = $('[data-sticky-button]', sticky);
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        stickyButton.tabIndex = show ? 0 : -1;
      }).observe(addButton);
    }
  }

  /* Recommendations ------------------------------------------------------- */

  function initRecommendations() {
    const el = $('[data-recommendations]');
    if (!el || el.children.length) return;
    const load = async () => {
      const url = `${window.theme.routes.recommendations}?section_id=${el.dataset.sectionId}&product_id=${el.dataset.productId}&limit=${el.dataset.limit}`;
      try {
        const html = await (await fetch(url)).text();
        const fresh = $('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
        if (fresh && fresh.children.length) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        } else {
          el.hidden = true;
        }
      } catch (error) {
        el.hidden = true;
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '600px' }
      );
      observer.observe(el);
    } else {
      load();
    }
  }

  /* Scroll-linked sections ------------------------------------------------ */

  // Runs `read` then `write` once per frame, only for elements that are on screen.
  // All reads happen before any write, so a frame never forces layout twice.
  function onScrollWhileVisible(elements, read, write) {
    const visible = new Set();
    let ticking = false;
    const update = () => {
      ticking = false;
      const viewport = window.innerHeight;
      const frames = Array.from(visible).map((el) => [el, read(el, viewport)]);
      frames.forEach(([el, data]) => write(el, data));
    };
    const request = () => {
      if (ticking || !visible.size) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
        request();
      },
      { rootMargin: '120px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  }

  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  // Journey: a thread sewn along the steps.
  function initJourney() {
    const sections = $$('[data-journey]');
    // Without motion the CSS shows the thread complete and every step visible.
    if (!sections.length || !motionEnabled) return;
    const wide = window.matchMedia('(min-width: 990px)');

    onScrollWhileVisible(
      sections,
      (el, viewport) => {
        const track = $('.journey__track', el);
        const rect = track.getBoundingClientRect();
        const horizontal = wide.matches;
        // Wide: all steps are on screen together, so finish quickly. Narrow: follow the reader down the list.
        const progress = clamp01(
          horizontal ? (viewport * 0.95 - rect.top) / (viewport * 0.38) : (viewport * 0.82 - rect.top) / rect.height
        );
        const steps = $$('.journey__step', track).map((step) => ({
          step,
          at: horizontal ? step.offsetLeft / track.offsetWidth : step.offsetTop / track.offsetHeight,
        }));
        return { progress, steps };
      },
      (el, { progress, steps }) => {
        el.style.setProperty('--p', progress.toFixed(3));
        steps.forEach(({ step, at }) => step.classList.toggle('is-active', progress >= 1 || progress > at + 0.03));
      }
    );
  }

  // Statement: words light up. Complete by the time the sentence is centred on screen.
  function initStatement() {
    const sections = $$('[data-statement]');
    if (!sections.length || !motionEnabled) return;
    onScrollWhileVisible(
      sections,
      (el, viewport) => {
        const rect = el.getBoundingClientRect();
        return clamp01((viewport * 0.92 - rect.top) / (viewport * 0.42 + rect.height / 2));
      },
      (el, progress) => el.style.setProperty('--p', progress.toFixed(3))
    );
  }

  // Expanding film: the pinned frame grows while the tall section scrolls past.
  function initExpand() {
    const sections = $$('[data-expand]');
    if (!sections.length) return;

    sections.forEach((el) => {
      const video = $('[data-expand-video]', el);
      const toggle = $('[data-expand-toggle]', el);
      if (!video || !toggle) return;
      const label = $('[data-expand-toggle-label]', toggle);
      const connection = navigator.connection || {};
      const lowData = connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '');
      let wanted = motionEnabled && !lowData; // autoplay only when motion is welcome and data is not constrained
      let onScreen = false;

      const sync = () => {
        const shouldPlay = wanted && onScreen;
        if (shouldPlay) {
          if (!video.src) {
            const wideScreen = window.matchMedia('(min-width: 990px)').matches;
            video.src = wideScreen && video.dataset.srcHd ? video.dataset.srcHd : video.dataset.src;
          }
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        } else {
          video.pause();
        }
        toggle.setAttribute('aria-pressed', String(wanted));
        label.textContent = wanted ? 'Pause film' : 'Play film';
      };

      video.addEventListener('playing', () => video.classList.add('is-playing'));
      video.addEventListener('error', () => {
        const hd = video.dataset.srcHd;
        if (hd && !video.src.endsWith(hd)) {
          video.src = hd;
          sync();
        }
      });
      toggle.addEventListener('click', () => {
        wanted = !wanted;
        sync();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            onScreen = entry.isIntersecting;
            sync();
          },
          { threshold: 0.2 }
        ).observe(el);
      } else {
        onScreen = true;
      }
      sync();
    });

    if (!motionEnabled) return;
    const headerOffset = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0;
    onScrollWhileVisible(
      sections,
      (el, viewport) => {
        const rect = el.getBoundingClientRect();
        const stage = $('.expand__stage', el);
        const travel = el.offsetHeight - stage.offsetHeight;
        // Begin a little before the stage pins so the growth feels continuous with the approach.
        const lead = viewport * 0.35;
        return clamp01((headerOffset() + lead - rect.top) / (travel + lead));
      },
      (el, progress) => el.style.setProperty('--p', progress.toFixed(3))
    );
  }

  /* Instagram: story panels and post strip -------------------------------- */

  function initSocial() {
    $$('[data-selector]').forEach((root) => {
      const panels = $$('[data-selector-panel]', root);
      const activate = (panel) => {
        panels.forEach((item) => {
          const active = item === panel;
          item.classList.toggle('is-active', active);
          $('.selector__toggle', item).setAttribute('aria-expanded', String(active));
        });
      };
      root.addEventListener('click', (event) => {
        const toggle = event.target.closest('.selector__toggle');
        if (toggle) activate(toggle.closest('[data-selector-panel]'));
      });
    });

    $$('[data-strip]').forEach((strip) => {
      const button = $('[data-strip-pause]', strip);
      if (!button) return;
      button.addEventListener('click', () => {
        const paused = strip.classList.toggle('is-paused');
        button.setAttribute('aria-pressed', String(paused));
        button.textContent = paused ? 'Play' : 'Pause';
      });
    });
  }

  /* Editorial reviews ----------------------------------------------------- */

  function initEditorialReviews() {
    $$('[data-ed-reviews]').forEach((root) => {
      const track = $('[data-ed-track]', root);
      const slides = $$('[data-ed-slide]', track);
      const prev = $('[data-ed-prev]', root);
      const next = $('[data-ed-next]', root);
      const current = $('[data-ed-current]', root);
      if (slides.length < 2 || !prev || !next) return;
      let index = 0;

      const setActive = (i) => {
        index = i;
        slides.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
        current.textContent = String(i + 1).padStart(2, '0');
        prev.disabled = i === 0;
        next.disabled = i === slides.length - 1;
      };
      const goTo = (i) => {
        track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      prev.addEventListener('click', () => goTo(Math.max(0, index - 1)));
      next.addEventListener('click', () => goTo(Math.min(slides.length - 1, index + 1)));
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
            });
          },
          { root: track, threshold: 0.6 }
        );
        slides.forEach((slide) => observer.observe(slide));
      }
      setActive(0);
    });
  }

  /* Hero video ------------------------------------------------------------ */

  function initHeroVideo() {
    const video = $('[data-hero-video]');
    if (!video || reduceMotion) return;
    const connection = navigator.connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return;
    const start = () => {
      $$('source[data-src]', video).forEach((source) => (source.src = source.dataset.src));
      video.load();
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };
    // Wait for the page to settle so the video never competes with first paint.
    if (document.readyState === 'complete') setTimeout(start, 600);
    else window.addEventListener('load', () => setTimeout(start, 600), { once: true });
  }

  /* Hero photo fan: on touch screens a tap opens the full photo; the link is an explicit button. -- */

  function initFan() {
    const dialog = $('[data-fan-view]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const touch = window.matchMedia('(hover: none)');
    const img = $('[data-fan-img]', dialog);
    const link = $('[data-fan-link]', dialog);
    let opener = null;

    document.addEventListener('click', (event) => {
      const a = event.target.closest('[data-fan-open]');
      if (!a || !touch.matches) return;
      event.preventDefault();
      opener = a;
      img.src = a.dataset.full;
      img.alt = a.dataset.alt || '';
      link.href = a.getAttribute('href');
      link.textContent = a.dataset.label ? `Shop ${a.dataset.label}` : 'Shop now';
      dialog.showModal();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-fan-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => opener && opener.focus());
  }

  /* Product grids that reveal four more at a time ------------------------- */

  function initLoadMore() {
    $$('[data-load-more]').forEach((box) => {
      const btn = $('[data-load-more-btn]', box);
      const wrap = btn.parentElement;
      const hiddenItems = () => $$('.pgrid__item.is-extra', box);
      if (!hiddenItems().length) return;
      btn.hidden = false;
      btn.addEventListener('click', () => {
        hiddenItems().slice(0, 4).forEach((item) => item.classList.remove('is-extra'));
        if (!hiddenItems().length) wrap.hidden = true;
      });
    });
  }

  /* Phone overlays -------------------------------------------------------- */

  // Shopify's cookie bar, the Inbox chat button and the Shopify Forms discount tab are all pinned
  // to the bottom of the screen by code the theme does not own. On phones, base.css turns the
  // cookie banner into a slim bar; this keeps the two floating buttons above it and makes the
  // discount tab smaller.
  function initOverlays() {
    const phone = window.matchMedia('(max-width: 699px)');
    const root = document.documentElement;
    let banner = null;
    let sized = null;

    const measure = () => {
      const showing = banner && phone.matches && getComputedStyle(banner).display !== 'none';
      root.style.setProperty('--overlay-lift', showing ? `${banner.offsetHeight}px` : '0px');
    };

    const watchBanner = () => {
      const found = document.getElementById('shopify-pc__banner');
      if (!found || found === banner) return;
      banner = found;
      new MutationObserver(measure).observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      if ('ResizeObserver' in window) {
        if (sized) sized.disconnect();
        sized = new ResizeObserver(measure);
        sized.observe(banner);
      }
      measure();
    };

    // The discount tab lives in an open shadow root, so page CSS cannot reach it; give it a small
    // stylesheet of its own. It only touches the closed tab, never the form it opens.
    const styleTeaser = (host, tries = 0) => {
      if (host.dataset.themeStyled) return;
      const shadow = host.shadowRoot;
      if (!shadow) {
        if (tries < 20) setTimeout(() => styleTeaser(host, tries + 1), 250);
        return;
      }
      host.dataset.themeStyled = 'true';
      const style = document.createElement('style');
      style.textContent = `@media (max-width: 699px) {
        #app-embed[data-current-step="tease"] { bottom: var(--overlay-lift, 0px) !important; padding-block: 12px !important; }
        #app-embed[data-current-step="tease"] [data-testid="form-teaser"] { padding: 10px 12px !important; gap: 8px !important; margin-inline: 12px !important; }
        #app-embed[data-current-step="tease"] [data-testid="form-teaser"] h3 { font-size: 0.875rem !important; line-height: 1.2 !important; }
      }`;
      shadow.appendChild(style);
    };

    const scan = () => {
      watchBanner();
      $$('shopify-forms-embed').forEach((host) => styleTeaser(host));
    };

    new MutationObserver(scan).observe(document.body, { childList: true });
    phone.addEventListener('change', measure);
    scan();
  }

  /* Footer ---------------------------------------------------------------- */

  // Menu columns are tap-to-open rows on phones and always open on wide screens.
  function initFooter() {
    const folds = $$('[data-footer-fold]');
    if (!folds.length) return;
    const wide = window.matchMedia('(min-width: 990px)');
    const sync = () => folds.forEach((fold) => (fold.open = wide.matches));
    sync();
    wide.addEventListener('change', sync);
    folds.forEach((fold) =>
      $('summary', fold).addEventListener('click', (event) => {
        if (wide.matches) event.preventDefault();
      })
    );
  }

  /* Misc ------------------------------------------------------------------ */

  function initMisc() {
    const autofocus = $('[data-autofocus]');
    if (autofocus) autofocus.focus();

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-submit-on-change]')) event.target.form.submit();
      if (event.target.matches('[data-sort-by]')) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
    });
  }

  function init() {
    initReveal();
    initHeader();
    initAnnouncements();
    initDrawers();
    initCart();
    initProduct();
    initRecommendations();
    initJourney();
    initStatement();
    initExpand();
    initEditorialReviews();
    initSocial();
    initHeroVideo();
    initFan();
    initLoadMore();
    initOverlays();
    initFooter();
    initMisc();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run reveals when sections are re-rendered.
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
})();
